class LaborCategoryDropdownController {
  constructor($scope, $rootScope, $http, $timeout, $element) {
    "ngInject";
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$http = $http;
    this.$timeout = $timeout;
    this.$element = $element;
    
    this.searchText = '';
    this.isOpen = false;
    this.laborCategories = [];
    this.filteredLaborCategories = [];
    this.loading = false;
    this.page = 0;
    this.pageSize = 20;
    this.hasMore = true;
    this.allLaborCategoriesLoaded = false;
    this.mouseInDropdown = false;
    
    // Initialize search text if labor category is pre-selected
    if (this.selectedLaborCategory) {
      this.searchText = this.selectedLaborCategory.name || '';
    }
    
    // If laborCategories are passed directly, use them; otherwise load from API
    if (this.laborCategories && Array.isArray(this.laborCategories) && this.laborCategories.length > 0) {
      this.laborCategories = this.sortLaborCategories(this.laborCategories);
      this.allLaborCategoriesLoaded = true;
      this.filterLaborCategories();
    } else if (this.contractId) {
      // Load initial labor categories from API
      this.loadLaborCategories();
    } else {
      // Watch for laborCategories to be set externally
      $scope.$watch(() => this.laborCategories, (newVal) => {
        if (newVal && Array.isArray(newVal) && newVal.length > 0 && !this.allLaborCategoriesLoaded) {
          this.laborCategories = this.sortLaborCategories(newVal);
          this.allLaborCategoriesLoaded = true;
          this.filterLaborCategories();
        }
      }, true);
    }
    
    // Watch for changes to selectedLaborCategory from outside
    $scope.$watch(() => this.selectedLaborCategory, (newVal) => {
      if (newVal && newVal.name) {
        this.searchText = newVal.name;
      } else if (!newVal) {
        this.searchText = '';
      }
    });
    
    // Setup scroll listener after view init
    $scope.$on('$destroy', () => {
      if (this.scrollElement) {
        this.scrollElement.removeEventListener('scroll', this.scrollHandler);
      }
    });
  }
  
  sortLaborCategories(laborCategories) {
    if (!laborCategories || !Array.isArray(laborCategories)) {
      return laborCategories;
    }
    
    // Create a copy to avoid mutating the original array
    const sorted = [...laborCategories];
    
    // Sort labor categories alphabetically by name
    sorted.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return sorted;
  }
  
  loadLaborCategories(reset = false) {
    if (this.loading || !this.contractId) return;
    
    this.loading = true;
    
    if (reset) {
      this.page = 0;
      this.laborCategories = [];
      this.hasMore = true;
    }
    
    let url = this.$rootScope.apiHost + "/api/admin/contracts/" + this.contractId + "/laborcategories";
    
    this.$http.get(url, { withCredentials: true })
      .then((response) => {
        let laborCategoriesData = response.data || [];
        
        // Sort labor categories alphabetically by name
        laborCategoriesData = this.sortLaborCategories(laborCategoriesData);
        
        if (reset) {
          this.laborCategories = laborCategoriesData;
        } else {
          this.laborCategories = this.sortLaborCategories([...this.laborCategories, ...laborCategoriesData]);
        }
        
        this.allLaborCategoriesLoaded = true;
        this.filterLaborCategories();
        this.loading = false;
      })
      .catch((error) => {
        console.error('Error loading labor categories:', error);
        this.loading = false;
      });
  }
  
  filterLaborCategories() {
    if (!this.searchText || this.searchText.trim() === '') {
      this.filteredLaborCategories = this.laborCategories.slice(0, this.pageSize);
      this.hasMore = this.laborCategories.length > this.pageSize;
    } else {
      const searchLower = this.searchText.toLowerCase();
      this.filteredLaborCategories = this.laborCategories.filter(laborCategory => 
        laborCategory.name && laborCategory.name.toLowerCase().includes(searchLower)
      ).slice(0, this.pageSize);
      this.hasMore = false; // Don't show "load more" when filtering
    }
  }
  
  onSearchChange() {
    this.filterLaborCategories();
  }
  
  onInputFocus() {
    this.isOpen = true;
    this.filterLaborCategories();
    this.setupScrollHandler();
  }
  
  setupScrollHandler() {
    // Setup scroll handler when dropdown opens
    this.$timeout(() => {
      const element = this.$element[0].querySelector('.labor-category-dropdown-list');
      if (element && !this.scrollElement) {
        this.scrollElement = element;
        this.scrollHandler = (event) => {
          this.onScroll(event);
          this.$scope.$apply();
        };
        element.addEventListener('scroll', this.scrollHandler);
      }
    }, 50);
  }
  
  onInputBlur() {
    // Delay closing to allow click events to fire
    this.$timeout(() => {
      if (!this.mouseInDropdown) {
        this.isOpen = false;
      }
    }, 200);
  }
  
  onDropdownMouseEnter() {
    this.mouseInDropdown = true;
  }
  
  onDropdownMouseLeave() {
    this.mouseInDropdown = false;
    this.isOpen = false;
  }
  
  selectLaborCategory(laborCategory) {
    this.selectedLaborCategory = laborCategory;
    this.searchText = laborCategory.name;
    this.isOpen = false;
    
    if (this.onSelect) {
      this.onSelect({ laborCategory: laborCategory });
    }
  }
  
  clearSelection() {
    this.selectedLaborCategory = null;
    this.searchText = '';
    this.isOpen = false;
    
    if (this.onSelect) {
      this.onSelect({ laborCategory: null });
    }
  }
  
  onScroll(event) {
    const element = event.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Load more when scrolled to within 50px of bottom
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      this.loadMore();
    }
  }
  
  loadMore() {
    if (this.loading || !this.hasMore || (this.searchText && this.searchText.trim() !== '')) return;
    
    this.page++;
    const startIndex = this.page * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    
    const remainingLaborCategories = this.laborCategories.slice(startIndex, endIndex);
    if (remainingLaborCategories.length > 0) {
      this.filteredLaborCategories = [...this.filteredLaborCategories, ...remainingLaborCategories];
      this.hasMore = endIndex < this.laborCategories.length;
    } else {
      this.hasMore = false;
    }
  }
  
  isSelected(laborCategory) {
    return this.selectedLaborCategory && this.selectedLaborCategory.id === laborCategory.id;
  }
}

export default LaborCategoryDropdownController;




// WEBPACK FOOTER //
// ./client/common/directives/labor-category-dropdown/labor-category-dropdown.controller.js